import React, { useState, useEffect, useRef } from "react";

import FormField from "@/components/atoms/FormField";
import { applyDocumentMask } from "@/helpers/document-helpers";

interface ManualMaskInputProps {
  mask: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  required?: boolean;
  variant?: "standard" | "outlined" | "filled";
  name: string;
  label: string;
  fullWidth?: boolean;
  InputLabelProps?: object;
  error?: boolean;
  helperText?: string;
  dynamicMask?: boolean;
  [key: string]: any;
}

const ManualMaskInput: React.FC<ManualMaskInputProps> = ({
  mask,
  value,
  onChange,
  disabled = false,
  required = false,
  variant = "standard",
  name,
  label,
  fullWidth = true,
  InputLabelProps = { shrink: true },
  error = false,
  helperText = "",
  dynamicMask = false,
  ...otherProps
}) => {
  const [maskedValue, setMaskedValue] = useState("");
  const isUserInput = useRef(false);
  const previousValue = useRef(value);
  const previousMask = useRef(mask);

  const applyMask = (inputValue: string, maskPattern: string): string => {
    if (!inputValue) return "";
    if (!maskPattern) return inputValue;

    const numbers = inputValue.replace(/\D/g, "");

    if (!numbers) return "";

    let result = "";
    let numberIndex = 0;

    for (let i = 0; i < maskPattern.length && numberIndex < numbers.length; i++) {
      if (maskPattern[i] === "9") {
        result += numbers[numberIndex];
        numberIndex++;
      } else {
        result += maskPattern[i];
      }
    }

    return result;
  };

  useEffect(() => {
    if (maskedValue === "" && value) {
      const digitsOnly = String(value).replace(/\D/g, "");
      if (dynamicMask) {
        const calculatedMask = applyDocumentMask(digitsOnly);
        const maskToUse = calculatedMask || mask;
        const masked = applyMask(digitsOnly, maskToUse);
        setMaskedValue(masked);
      } else {
        if (!mask) {
          setMaskedValue(value);
        } else if (digitsOnly) {
          const masked = applyMask(digitsOnly, mask);
          setMaskedValue(masked);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (!dynamicMask) {
      if (value !== undefined && value !== null && value !== "") {
        const digitsOnly = String(value).replace(/\D/g, "");
        if (!mask) {
          setMaskedValue(value);
          return;
        }
        if (!digitsOnly) {
          setMaskedValue("");
          return;
        }
        const masked = applyMask(digitsOnly, mask);
        setMaskedValue(masked);
      } else {
        setMaskedValue("");
      }
      return;
    }

    const maskChanged = previousMask.current !== mask;
    previousMask.current = mask;

    if (isUserInput.current && !maskChanged) {
      isUserInput.current = false;
      previousValue.current = value;
      return;
    }

    if (isUserInput.current) {
      isUserInput.current = false;
      previousValue.current = value;
      if (!maskChanged) {
        return;
      }
    }

    if (previousValue.current === value && !maskChanged) {
      return;
    }

    previousValue.current = value;

    if (value !== undefined && value !== null && value !== "") {
      const digitsOnly = String(value).replace(/\D/g, "");

      if (!mask) {
        setMaskedValue(value);
        return;
      }

      if (!digitsOnly) {
        setMaskedValue("");
        return;
      }

      const masked = applyMask(digitsOnly, mask);
      setMaskedValue(masked);
    } else {
      setMaskedValue("");
    }
  }, [value, mask]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    const digitsOnly = inputValue.replace(/\D/g, "");

    let limitedDigits = digitsOnly;
    let maskToUse = mask;

    if (dynamicMask) {
      const maxLength = 14;
      limitedDigits = digitsOnly.slice(0, maxLength);

      const calculatedMask = applyDocumentMask(limitedDigits);
      maskToUse = calculatedMask || mask;
    }

    const masked = applyMask(limitedDigits, maskToUse);

    if (dynamicMask) {
      isUserInput.current = true;
    }

    setMaskedValue(masked);

    const syntheticEvent = {
      ...event,
      target: {
        ...event.target,
        name: name,
        value: masked,
      },
    };

    onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <FormField
      {...otherProps}
      value={maskedValue}
      onChange={handleInputChange}
      disabled={disabled}
      required={required}
      variant={variant}
      name={name}
      label={label}
      fullWidth={fullWidth}
      InputLabelProps={InputLabelProps}
      error={error}
      helperText={helperText}
    />
  );
};

export default ManualMaskInput;
